import { useCallback, useId, useLayoutEffect, useRef } from "react";

import { cn } from "../lib/cn";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  /** Its presence puts the field in the invalid state. */
  error?: string;
  /** Passing `minRows` or `maxRows` turns on auto-grow. */
  minRows?: number;
  maxRows?: number;
  ref?: React.Ref<HTMLTextAreaElement>;
}

export function Textarea({
  label,
  hint,
  error,
  id,
  className,
  rows,
  minRows,
  maxRows,
  value,
  onInput,
  ref,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: TextareaProps) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  const hintId = `${textareaId}-hint`;
  const errorId = `${textareaId}-error`;

  const autoGrow = minRows !== undefined || maxRows !== undefined;
  const renderedRows = autoGrow ? (minRows ?? rows ?? 1) : (rows ?? 4);

  const innerRef = useRef<HTMLTextAreaElement | null>(null);

  const fit = useCallback(() => {
    const el = innerRef.current;
    if (!el || !autoGrow) return;

    const styles = window.getComputedStyle(el);
    // `line-height: normal` doesn't resolve to a length.
    const lineHeight =
      parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.2 || 0;
    const paddingY =
      parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    const borderY =
      parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
    // `scrollHeight` is content + padding; `height` follows `box-sizing`.
    const contentBox = styles.boxSizing === "content-box";
    const trim = contentBox ? -paddingY : borderY;
    const chrome = contentBox ? 0 : paddingY + borderY;

    const minHeight = minRows === undefined ? 0 : minRows * lineHeight + chrome;
    const maxHeight =
      maxRows === undefined ? Infinity : maxRows * lineHeight + chrome;

    // Collapse first, or `scrollHeight` can't fall below the height we set.
    el.style.height = "auto";
    const naturalHeight = el.scrollHeight + trim;

    el.style.height = `${Math.min(Math.max(naturalHeight, minHeight), maxHeight)}px`;
    el.style.overflowY = naturalHeight > maxHeight ? "auto" : "hidden";
  }, [autoGrow, minRows, maxRows]);

  // `onInput` covers uncontrolled typing; this covers value, bounds and mount.
  useLayoutEffect(fit, [fit, value]);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el || !autoGrow) return;

    // Width only: height changes are our own writes.
    let lastWidth = el.clientWidth;
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            if (el.clientWidth === lastWidth) return;
            lastWidth = el.clientWidth;
            fit();
          });
    observer?.observe(el);

    // The fallback face measures differently from the swapped-in font.
    let live = true;
    void document.fonts?.ready.then(() => {
      if (live) fit();
    });

    return () => {
      live = false;
      observer?.disconnect();
    };
  }, [autoGrow, fit]);

  const describedBy =
    [ariaDescribedBy, hint ? hintId : null, error ? errorId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  const setRef = (node: HTMLTextAreaElement | null) => {
    innerRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      (ref as React.RefObject<HTMLTextAreaElement | null>).current = node;
    }
  };

  return (
    <div className="flex w-full flex-col gap-1.5">
      {label && (
        <label
          htmlFor={textareaId}
          className="font-body text-sm font-medium text-ink"
        >
          {label}
        </label>
      )}
      <textarea
        ref={setRef}
        id={textareaId}
        rows={renderedRows}
        value={value}
        onInput={(event) => {
          fit();
          onInput?.(event);
        }}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          "w-full rounded-paper border bg-card px-3 py-2",
          "font-body text-sm text-ink placeholder:text-placeholder",
          "transition duration-150 ease-paper",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
          "disabled:cursor-not-allowed disabled:bg-recess disabled:text-faint",
          autoGrow ? "resize-none" : "resize-y",
          error ? "border-ink-stopped" : "border-rule",
          className,
        )}
        {...rest}
      />
      {hint && (
        <p id={hintId} className="font-body text-sm text-annotation">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="font-body text-sm text-ink-stopped">
          {error}
        </p>
      )}
    </div>
  );
}
